import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../config';
import {
	ShieldCheck,
	ShieldAlert,
	Database,
	CloudUpload,
	Calendar as CalendarIcon,
	Settings2,
	Play,
	QrCode,
	Copy,
	CheckCircle2,
	RefreshCcw,
	ShieldQuestion,
	Activity,
	Info,
	ToggleLeft
} from 'lucide-react';
import {
	Alert,
	AlertColor,
	Snackbar,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField
} from '@mui/material';

interface TwoFactorSetup {
	secret: string;
	qrCodeDataURL: string;
}

const Settings: React.FC = () => {
	const [is2FAEnabled, setIs2FAEnabled] = useState<boolean>(false);
	const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);
	const [backupStatus, setBackupStatus] = useState<{ scheduleType: string; time: string; isEnabled: boolean } | null>(null);
	const [backupLoading, setBackupLoading] = useState(false);
	const [isBackupEditing, setIsBackupEditing] = useState(false);
	const [backupEditData, setBackupEditData] = useState({ type: 'daily', time: '02:00' });
	const [loading, setLoading] = useState<boolean>(true);
	const [setupData, setSetupData] = useState<TwoFactorSetup | null>(null);
	const [verificationCode, setVerificationCode] = useState<string>("");
	const [showSetupDialog, setShowSetupDialog] = useState<boolean>(false);
	const [setupLoading, setSetupLoading] = useState<boolean>(false);

	// Feature flag: Total Earnings button (Home screen) — BusinessRules key TOTAL_EARNINGS_BUTTON_ENABLED
	const [totalEarningsEnabled, setTotalEarningsEnabled] = useState<boolean | null>(null);
	// Feature flag: Offers section (Home screen) — BusinessRules key OFFERS_ENABLED
	const [offersEnabled, setOffersEnabled] = useState<boolean | null>(null);
	// Total Earnings eligibility window (signup-date based). Empty = no limit on that side.
	const [teStartDate, setTeStartDate] = useState<string>('');
	const [teEndDate, setTeEndDate] = useState<string>('');
	const [teWindowLoading, setTeWindowLoading] = useState<boolean>(false);
	const [offersLoading, setOffersLoading] = useState<boolean>(false);
	const [flagLoading, setFlagLoading] = useState<boolean>(false);
	const [flagDescription, setFlagDescription] = useState<string>('');

	const [snackbar, setSnackbar] = useState<{
		open: boolean;
		message: string;
		severity: AlertColor;
	}>({
		open: false,
		message: '',
		severity: 'success'
	});

	useEffect(() => {
		fetchUserData();
		fetchBackupStatus();
		fetchFeatureFlag();
	}, []);

	const fetchFeatureFlag = async () => {
		try {
			const token = localStorage.getItem('token');
			const res = await axios.get(`${API_BASE}/api/business-rules`, {
				headers: { Authorization: `Bearer ${token}` }
			});
			if (res.data.status === 'success' && Array.isArray(res.data.data)) {
				const rule = res.data.data.find((r: any) => r.key === 'TOTAL_EARNINGS_BUTTON_ENABLED');
				if (rule) {
					setTotalEarningsEnabled(!!rule.value);
					setFlagDescription(rule.description || '');
				} else {
					// Rule missing: apps default to HIDING the button
					setTotalEarningsEnabled(false);
				}
				const offersRule = res.data.data.find((r: any) => r.key === 'OFFERS_ENABLED');
				// Rule missing: apps default to HIDING Offers
				setOffersEnabled(offersRule ? !!offersRule.value : false);
				const teStart = res.data.data.find((r: any) => r.key === 'TOTAL_EARNINGS_START_DATE');
				const teEnd = res.data.data.find((r: any) => r.key === 'TOTAL_EARNINGS_END_DATE');
				setTeStartDate(teStart?.value ? String(teStart.value).slice(0, 10) : '');
				setTeEndDate(teEnd?.value ? String(teEnd.value).slice(0, 10) : '');
			}
		} catch (error) {
			console.error('Error fetching feature flag:', error);
		}
	};

	const toggleTotalEarnings = async () => {
		if (totalEarningsEnabled === null) return;
		const next = !totalEarningsEnabled;
		setFlagLoading(true);
		try {
			const token = localStorage.getItem('token');
			const res = await axios.put(
				`${API_BASE}/api/business-rules/TOTAL_EARNINGS_BUTTON_ENABLED`,
				{ value: next },
				{ headers: { Authorization: `Bearer ${token}` } }
			);
			if (res.data.status === 'success') {
				setTotalEarningsEnabled(next);
				showSnackbar(`Total Earnings button ${next ? 'enabled (visible)' : 'disabled (hidden)'}`, 'success');
			} else {
				showSnackbar('Failed to update setting', 'error');
			}
		} catch (error) {
			showSnackbar('Failed to update setting', 'error');
		} finally {
			setFlagLoading(false);
		}
	};

	const toggleOffers = async () => {
		if (offersEnabled === null) return;
		const next = !offersEnabled;
		setOffersLoading(true);
		try {
			const token = localStorage.getItem('token');
			const res = await axios.put(
				`${API_BASE}/api/business-rules/OFFERS_ENABLED`,
				{ value: next },
				{ headers: { Authorization: `Bearer ${token}` } }
			);
			if (res.data.status === 'success') {
				setOffersEnabled(next);
				showSnackbar(`Offers section ${next ? 'enabled (visible)' : 'disabled (hidden)'}`, 'success');
			} else {
				showSnackbar('Failed to update setting', 'error');
			}
		} catch (error) {
			showSnackbar('Failed to update setting', 'error');
		} finally {
			setOffersLoading(false);
		}
	};

	const saveTotalEarningsWindow = async () => {
		if (teStartDate && teEndDate && teStartDate > teEndDate) {
			showSnackbar('Start date cannot be after end date', 'error');
			return;
		}
		setTeWindowLoading(true);
		try {
			const token = localStorage.getItem('token');
			const headers = { headers: { Authorization: `Bearer ${token}` } };
			await axios.put(`${API_BASE}/api/business-rules/TOTAL_EARNINGS_START_DATE`,
				{ value: teStartDate, description: 'Total Earnings button eligibility window (start, signup-date based; empty = from beginning)' }, headers);
			await axios.put(`${API_BASE}/api/business-rules/TOTAL_EARNINGS_END_DATE`,
				{ value: teEndDate, description: 'Total Earnings button eligibility window (end, signup-date based; empty = no limit)' }, headers);
			showSnackbar('Eligibility window saved', 'success');
		} catch (error) {
			showSnackbar('Failed to save eligibility window', 'error');
		} finally {
			setTeWindowLoading(false);
		}
	};

	const fetchUserData = async () => {
		setLoading(true);
		try {
			const email = localStorage.getItem('email');
			const response = await axios.get(`${API_BASE}/api/auth/me`, {
				headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
			});
			if (response.data.status === 'success') {
				setIs2FAEnabled(response.data.twoFactorEnabled);
				setIsSuperAdmin(response.data.isSuperAdmin);
			}
		} catch (error: any) {
			console.error("Error fetching user data:", error);
			showSnackbar("Failed to load settings", "error");
		} finally {
			setLoading(false);
		}
	};

	const showSnackbar = (message: string, severity: AlertColor) => {
		setSnackbar({ open: true, message, severity });
	};

	const initiate2FASetup = async () => {
		setSetupLoading(true);
		try {
			const response = await axios.post(`${API_BASE}/api/auth/2fa/setup`, {}, {
				headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
			});
			if (response.data.status === 'success') {
				setSetupData({
					secret: response.data.secret,
					qrCodeDataURL: response.data.qrCodeDataURL
				});
				setShowSetupDialog(true);
			}
		} catch (error: any) {
			showSnackbar("Failed to initiate 2FA setup", "error");
		} finally {
			setSetupLoading(false);
		}
	};

	const verifyAndEnable = async () => {
		if (verificationCode.length !== 6) {
			showSnackbar("Enter a valid 6-digit code", "error");
			return;
		}

		setSetupLoading(true);
		try {
			const response = await axios.post(`${API_BASE}/api/auth/2fa/enable`,
				{ token: verificationCode },
				{ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
			);

			if (response.data.status === 'success') {
				setIs2FAEnabled(true);
				setShowSetupDialog(false);
				setVerificationCode("");
				setSetupData(null);
				showSnackbar("Two-Factor Authentication enabled successfully", "success");
			} else {
				showSnackbar(response.data.message || "Invalid verification code", "error");
			}
		} catch (error: any) {
			showSnackbar("Verification failed", "error");
		} finally {
			setSetupLoading(false);
		}
	};

	const disable2FA = async () => {
		if (!window.confirm("Are you sure you want to disable Two-Factor Authentication? This will reduce your account security.")) {
			return;
		}

		setLoading(true);
		try {
			const response = await axios.post(`${API_BASE}/api/auth/2fa/disable`, {}, {
				headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
			});
			if (response.data.status === 'success') {
				setIs2FAEnabled(false);
				showSnackbar("Two-Factor Authentication has been disabled", "success");
			}
		} catch (error: any) {
			showSnackbar("Failed to disable 2FA", "error");
		} finally {
			setLoading(false);
		}
	};

	const fetchBackupStatus = async () => {
		try {
			const token = localStorage.getItem("token");
			const response = await axios.get(`${API_BASE}/api/backup/status`, {
				headers: { Authorization: `Bearer ${token}` }
			});
			if (response.data.status) {
				setBackupStatus(response.data.status);
				setBackupEditData({
					type: response.data.status.scheduleType,
					time: response.data.status.time
				});
			}
		} catch (error) {
			console.error("Error fetching backup status:", error);
		}
	};

	const toggleBackup = async () => {
		if (!backupStatus) return;
		setBackupLoading(true);
		try {
			const token = localStorage.getItem("token");
			await axios.post(`${API_BASE}/api/backup/toggle`,
				{ enabled: !backupStatus.isEnabled },
				{ headers: { Authorization: `Bearer ${token}` } }
			);
			setBackupStatus(s => s && { ...s, isEnabled: !s.isEnabled });
			showSnackbar(`Auto-backup ${!backupStatus.isEnabled ? 'enabled' : 'disabled'}`, "success");
		} catch (error) {
			showSnackbar("Failed to toggle backup", "error");
		} finally {
			setBackupLoading(false);
		}
	};

	const runManualBackup = async () => {
		setBackupLoading(true);
		try {
			const token = localStorage.getItem("token");
			await axios.post(`${API_BASE}/api/backup/manual`, {}, {
				headers: { Authorization: `Bearer ${token}` }
			});
			showSnackbar("Manual backup initiated successfully", "success");
		} catch (error) {
			showSnackbar("Failed to start manual backup", "error");
		} finally {
			setBackupLoading(false);
		}
	};

	const saveBackupSchedule = async () => {
		setBackupLoading(true);
		try {
			const token = localStorage.getItem("token");
			await axios.post(`${API_BASE}/api/backup/schedule`,
				{ type: backupEditData.type, time: backupEditData.time },
				{ headers: { Authorization: `Bearer ${token}` } }
			);
			setBackupStatus(s => s && ({ ...s, scheduleType: backupEditData.type, time: backupEditData.time }));
			setIsBackupEditing(false);
			showSnackbar("Backup schedule updated", "success");
		} catch (error) {
			showSnackbar("Failed to update schedule", "error");
		} finally {
			setBackupLoading(false);
		}
	};

	const copySecret = () => {
		if (setupData?.secret) {
			navigator.clipboard.writeText(setupData.secret);
			showSnackbar("Secret key copied to clipboard", "success");
		}
	};

	if (loading) {
		return (
			<div className="flex items-center justify-center min-h-[400px]">
				<CircularProgress sx={{ color: '#523680' }} />
			</div>
		);
	}

	return (
		<div className="max-w-4xl mx-auto p-4 md:p-8">
			<div className="mb-6 md:mb-10">
				<h1 className="text-xl md:text-3xl font-black text-gray-900 mb-2 uppercase tracking-tight">Account Settings</h1>
				<p className="text-xs md:text-base text-gray-500 font-bold uppercase tracking-widest opacity-60">Security preferences and profile settings</p>
			</div>

			<div className="space-y-4 md:space-y-8">
				{/* Security Section */}
				<div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden">
					<div className="p-4 md:p-8 border-b border-gray-50 flex items-center gap-4 bg-gray-50/30">
						<div className="p-3 bg-purple-100 rounded-2xl text-[#523680]">
							<ShieldCheck className="w-6 h-6 md:w-8 md:h-8" />
						</div>
						<div>
							<h2 className="text-sm md:text-xl font-black text-gray-900 uppercase tracking-tight">Security & Privacy</h2>
							<p className="text-[10px] md:text-sm text-gray-400 font-bold uppercase tracking-widest">Protect your account from unauthorized access</p>
						</div>
					</div>

					<div className="p-4 md:p-10">
						<div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 p-4 md:p-8 bg-gray-50/50 rounded-3xl border border-gray-100">
							<div className="flex items-start gap-4">
								<div className={`mt-1 p-3 rounded-2xl ${is2FAEnabled ? 'bg-green-100/50' : 'bg-amber-100/50'}`}>
									{is2FAEnabled ?
										<ShieldCheck className="w-6 h-6 text-green-600" /> :
										<ShieldAlert className="w-6 h-6 text-amber-600" />
									}
								</div>
								<div className="min-w-0">
									<h3 className="text-sm md:text-lg font-black text-gray-900 uppercase tracking-tight mb-1">Two-Factor Auth (2FA)</h3>
									<p className="text-[11px] md:text-sm text-gray-500 font-medium leading-relaxed mb-3">
										Adds an extra layer of security requiring a code from Google Authenticator when you sign in.
									</p>
									<span className={`inline-flex items-center px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${is2FAEnabled ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-gray-100 text-gray-500 border border-gray-200'
										}`}>
										{is2FAEnabled ? 'Protected' : 'Unsecured'}
									</span>
								</div>
							</div>

							<div className="shrink-0">
								{is2FAEnabled ? (
									<button
										onClick={disable2FA}
										className="w-full md:w-auto px-8 py-3 bg-red-50 text-red-600 hover:bg-red-100 font-black text-[10px] md:text-xs uppercase tracking-widest rounded-xl transition-all border border-red-100 shadow-sm active:scale-95"
									>
										Disable Security
									</button>
								) : (
									<button
										onClick={initiate2FASetup}
										disabled={setupLoading}
										className="w-full md:w-auto px-10 py-4 bg-[#523680] text-white hover:bg-[#3f2a60] font-black text-[10px] md:text-xs uppercase tracking-widest rounded-xl shadow-lg shadow-purple-200 hover:shadow-xl hover:-translate-y-0.5 active:scale-95 transition-all disabled:opacity-70"
									>
										{setupLoading ? 'Initializing...' : 'Enable 2FA Now'}
									</button>
								)}
							</div>
						</div>

						<div className="mt-6 md:mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
							<div className="p-4 md:p-6 border border-gray-100 rounded-2xl flex gap-4 hover:shadow-md transition-shadow">
								<div className="p-2.5 bg-blue-50 rounded-xl h-fit">
									<Info className="w-5 h-5 text-blue-600" />
								</div>
								<div>
									<h4 className="text-[11px] md:text-sm font-black text-gray-900 uppercase tracking-tight">Hardware Tracking</h4>
									<p className="text-[10px] md:text-xs text-gray-400 font-bold uppercase tracking-widest mt-1">
										Session IP & device footprint logged
									</p>
								</div>
							</div>
							<div className="p-4 md:p-6 border border-gray-100 rounded-2xl flex gap-4 hover:shadow-md transition-shadow">
								<div className="p-2.5 bg-purple-50 rounded-xl h-fit">
									<RefreshCcw className="w-5 h-5 text-purple-600" />
								</div>
								<div>
									<h4 className="text-[11px] md:text-sm font-black text-gray-900 uppercase tracking-tight">Auto-Logout</h4>
									<p className="text-[10px] md:text-xs text-gray-400 font-bold uppercase tracking-widest mt-1">
										Expires after 30 mins inactivity
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* Backup Management Section - Only for Super Admin */}
				{isSuperAdmin && (
					<div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden">
						<div className="p-4 md:p-8 border-b border-gray-50 flex items-center gap-4 bg-gray-50/30">
							<div className="p-3 bg-indigo-100 rounded-2xl text-indigo-600">
								<Database className="w-6 h-6 md:w-8 md:h-8" />
							</div>
							<div>
								<h2 className="text-sm md:text-xl font-black text-gray-900 uppercase tracking-tight">System & Data Backup</h2>
								<p className="text-[10px] md:text-sm text-gray-400 font-bold uppercase tracking-widest">Manage automated data recovery points</p>
							</div>
						</div>

						<div className="p-4 md:p-10">
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
								{/* Backup Status Card */}
								<div className="bg-gray-50/50 p-6 rounded-3xl border border-gray-100 flex flex-col justify-between">
									<div className="flex justify-between items-start mb-4">
										<div>
											<span className="block text-[10px] text-gray-400 font-bold uppercase mb-1 tracking-widest">Current Status</span>
											<h3 className={`text-lg font-black uppercase tracking-tight ${backupStatus?.isEnabled ? 'text-green-600' : 'text-amber-600'}`}>
												{backupStatus?.isEnabled ? 'Automatic Backups Active' : 'Auto Backups Paused'}
											</h3>
										</div>
										<Activity className={`w-5 h-5 ${backupStatus?.isEnabled ? 'text-green-500 animate-pulse' : 'text-gray-300'}`} />
									</div>

									<div className="flex items-center gap-3">
										<button
											onClick={toggleBackup}
											disabled={backupLoading}
											className={`flex-1 py-3 px-4 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all active:scale-95 ${backupStatus?.isEnabled
													? 'bg-amber-50 text-amber-600 border border-amber-100 hover:bg-amber-100'
													: 'bg-green-50 text-green-600 border border-green-100 hover:bg-green-100'
												}`}
										>
											{backupStatus?.isEnabled ? 'Pause Automation' : 'Resume System'}
										</button>
										<button
											onClick={runManualBackup}
											disabled={backupLoading}
											className="flex items-center justify-center bg-[#523680] text-white p-3 rounded-xl shadow-lg shadow-purple-100 hover:shadow-purple-200 active:scale-90 transition-all"
											title="Run Manual Backup Now"
										>
											<Play className="w-5 h-5 fill-current" />
										</button>
									</div>
								</div>

								{/* Schedule Configuration Card */}
								<div className="bg-gray-50/50 p-6 rounded-3xl border border-gray-100">
									<div className="flex justify-between items-start mb-4">
										<div>
											<span className="block text-[10px] text-gray-400 font-bold uppercase mb-1 tracking-widest">Backup Schedule</span>
											<div className="flex items-center gap-2">
												<h3 className="text-lg font-black text-gray-900 uppercase tracking-tight">
													{backupStatus?.scheduleType || 'Daily'} @ {backupStatus?.time || '00:00'}
												</h3>
												<button
													onClick={() => setIsBackupEditing(!isBackupEditing)}
													className="p-1.5 text-indigo-400 hover:bg-indigo-50 rounded-lg transition-colors"
												>
													<Settings2 className="w-4 h-4" />
												</button>
											</div>
										</div>
										<CalendarIcon className="w-5 h-5 text-indigo-400" />
									</div>

									{isBackupEditing ? (
										<div className="space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
											<div className="grid grid-cols-2 gap-2">
												<div className="relative">
													<select
														value={backupEditData.type}
														onChange={(e) => setBackupEditData({ ...backupEditData, type: e.target.value })}
														className="w-full bg-white border border-gray-200 text-[10px] font-black uppercase tracking-tight p-2.5 rounded-xl appearance-none focus:ring-2 focus:ring-purple-200"
													>
														<option value="daily">Daily</option>
														<option value="weekly">Weekly</option>
														<option value="monthly">Monthly</option>
													</select>
												</div>
												<input
													type="time"
													value={backupEditData.time}
													onChange={(e) => setBackupEditData({ ...backupEditData, time: e.target.value })}
													className="w-full bg-white border border-gray-200 text-xs font-black p-2 rounded-xl focus:ring-2 focus:ring-purple-200"
												/>
											</div>
											<div className="flex gap-2">
												<button
													onClick={saveBackupSchedule}
													disabled={backupLoading}
													className="flex-1 py-2 bg-indigo-600 text-white text-[9px] font-black uppercase tracking-widest rounded-lg shadow-md active:scale-95"
												>
													{backupLoading ? 'Saving...' : 'Confirm'}
												</button>
												<button
													onClick={() => setIsBackupEditing(false)}
													className="px-4 py-2 bg-white text-gray-400 text-[9px] font-black uppercase tracking-widest rounded-lg border border-gray-100 hover:bg-gray-50"
												>
													Cancel
												</button>
											</div>
										</div>
									) : (
										<div className="flex items-center gap-2 p-3 bg-white/50 rounded-2xl border border-gray-50">
											<div className="w-2 h-2 rounded-full bg-indigo-400"></div>
											<p className="text-[10px] text-gray-500 font-bold uppercase tracking-tight">
												Next sync will run following the {backupStatus?.scheduleType} cadence
											</p>
										</div>
									)}
								</div>
							</div>

							{/* Info Grid */}
							<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
								{/* <div className="p-4 border border-gray-100 rounded-2xl bg-white flex items-center gap-3">
									<div className="p-2 bg-purple-50 rounded-xl">
										<CloudUpload className="w-4 h-4 text-purple-600" />
									</div>
									<div>
										<span className="block text-[8px] text-gray-400 font-bold uppercase tracking-widest">Storage</span>
										<p className="text-[10px] font-black text-gray-900 uppercase">Cloud S3</p>
									</div>
								</div> */}
								<div className="p-4 border border-gray-100 rounded-2xl bg-white flex items-center gap-3">
									<div className="p-2 bg-blue-50 rounded-xl">
										<RefreshCcw className="w-4 h-4 text-blue-600" />
									</div>
									<div>
										<span className="block text-[8px] text-gray-400 font-bold uppercase tracking-widest">Retention</span>
										<p className="text-[10px] font-black text-gray-900 uppercase">30 Days</p>
									</div>
								</div>
								<div className="p-4 border border-gray-100 rounded-2xl bg-white flex items-center gap-3">
									<div className="p-2 bg-green-50 rounded-xl">
										<CheckCircle2 className="w-4 h-4 text-green-600" />
									</div>
									<div>
										<span className="block text-[8px] text-gray-400 font-bold uppercase tracking-widest">Health</span>
										<p className="text-[10px] font-black text-gray-900 uppercase">Optimal</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				)}

					{/* App Feature Flags Section - Only for Super Admin */}
					{isSuperAdmin && (
						<div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden">
							<div className="p-4 md:p-8 border-b border-gray-50 flex items-center gap-4 bg-gray-50/30">
								<div className="p-3 bg-teal-100 rounded-2xl text-teal-600">
									<ToggleLeft className="w-6 h-6 md:w-8 md:h-8" />
								</div>
								<div>
									<h2 className="text-sm md:text-xl font-black text-gray-900 uppercase tracking-tight">App Feature Flags</h2>
									<p className="text-[10px] md:text-sm text-gray-400 font-bold uppercase tracking-widest">Control visibility of app features (web &amp; mobile)</p>
								</div>
							</div>

							<div className="p-4 md:p-10">
								<div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 p-4 md:p-8 bg-gray-50/50 rounded-3xl border border-gray-100">
									<div className="flex items-start gap-4 min-w-0">
										<div className={`mt-1 p-3 rounded-2xl ${totalEarningsEnabled ? 'bg-green-100/50' : 'bg-amber-100/50'}`}>
											<Activity className={`w-6 h-6 ${totalEarningsEnabled ? 'text-green-600' : 'text-amber-600'}`} />
										</div>
										<div className="min-w-0">
											<h3 className="text-sm md:text-lg font-black text-gray-900 uppercase tracking-tight mb-1">Total Earnings Button (Home Screen)</h3>
											<p className="text-[11px] md:text-sm text-gray-500 font-medium leading-relaxed mb-3">
												{flagDescription || 'Show/hide the Total Earnings button on the Home screen (web & mobile apps).'}
											</p>
											<span className={`inline-flex items-center px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${totalEarningsEnabled ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>
												{totalEarningsEnabled === null ? 'Loading...' : totalEarningsEnabled ? 'Visible' : 'Hidden'}
											</span>
										</div>
									</div>

									<div className="shrink-0">
										<button
											onClick={toggleTotalEarnings}
											disabled={flagLoading || totalEarningsEnabled === null}
											className={`w-full md:w-auto px-8 py-3 font-black text-[10px] md:text-xs uppercase tracking-widest rounded-xl transition-all border shadow-sm active:scale-95 disabled:opacity-60 ${totalEarningsEnabled ? 'bg-amber-50 text-amber-600 border-amber-100 hover:bg-amber-100' : 'bg-green-50 text-green-600 border-green-100 hover:bg-green-100'}`}
										>
											{flagLoading ? 'Saving...' : totalEarningsEnabled ? 'Hide Button' : 'Show Button'}
										</button>
									</div>
								</div>

								<div className="mt-6 p-4 md:p-8 bg-gray-50/50 rounded-3xl border border-gray-100">
									<h3 className="text-sm md:text-lg font-black text-gray-900 uppercase tracking-tight mb-1">Total Earnings — Eligibility Window</h3>
									<p className="text-[11px] md:text-sm text-gray-500 font-medium leading-relaxed mb-4">
										Only users whose <b>signup date</b> falls inside this window see the Total Earnings button (master toggle above must also be ON). Leave start empty for "from the beginning", end empty for "no limit".
									</p>
									<div className="flex flex-col md:flex-row md:items-end gap-4">
										<div>
											<label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Start Date (optional)</label>
											<input type="date" value={teStartDate} onChange={(e) => setTeStartDate(e.target.value)}
												className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-bold text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/30" />
										</div>
										<div>
											<label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">End Date (optional)</label>
											<input type="date" value={teEndDate} onChange={(e) => setTeEndDate(e.target.value)}
												className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-bold text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/30" />
										</div>
										<button onClick={saveTotalEarningsWindow} disabled={teWindowLoading}
											className="px-8 py-3 font-black text-[10px] md:text-xs uppercase tracking-widest rounded-xl transition-all border shadow-sm active:scale-95 disabled:opacity-60 bg-primary/5 text-primary border-primary/10 hover:bg-primary/10">
											{teWindowLoading ? 'Saving...' : 'Save Window'}
										</button>
										{(teStartDate || teEndDate) && (
											<button onClick={() => { setTeStartDate(''); setTeEndDate(''); }} disabled={teWindowLoading}
												className="px-6 py-3 font-black text-[10px] md:text-xs uppercase tracking-widest rounded-xl transition-all border shadow-sm active:scale-95 disabled:opacity-60 bg-gray-50 text-gray-500 border-gray-100 hover:bg-gray-100">
												Clear
											</button>
										)}
									</div>
									<p className="text-[10px] md:text-xs text-gray-400 font-bold mt-3">
										Current: {teStartDate || 'from beginning'} → {teEndDate || 'no limit'} (after clearing, press Save Window to apply)
									</p>
								</div>

								<div className="mt-6 flex flex-col lg:flex-row lg:items-center justify-between gap-6 p-4 md:p-8 bg-gray-50/50 rounded-3xl border border-gray-100">
									<div className="flex items-start gap-4 min-w-0">
										<div className={`mt-1 p-3 rounded-2xl ${offersEnabled ? 'bg-green-100/50' : 'bg-amber-100/50'}`}>
											<Activity className={`w-6 h-6 ${offersEnabled ? 'text-green-600' : 'text-amber-600'}`} />
										</div>
										<div className="min-w-0">
											<h3 className="text-sm md:text-lg font-black text-gray-900 uppercase tracking-tight mb-1">Offers Section (Home Screen)</h3>
											<p className="text-[11px] md:text-sm text-gray-500 font-medium leading-relaxed mb-3">
												Show/hide the Offers icon on the Home screen (web &amp; mobile apps).
											</p>
											<span className={`inline-flex items-center px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${offersEnabled ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>
												{offersEnabled === null ? 'Loading...' : offersEnabled ? 'Visible' : 'Hidden'}
											</span>
										</div>
									</div>

									<div className="shrink-0">
										<button
											onClick={toggleOffers}
											disabled={offersLoading || offersEnabled === null}
											className={`w-full md:w-auto px-8 py-3 font-black text-[10px] md:text-xs uppercase tracking-widest rounded-xl transition-all border shadow-sm active:scale-95 disabled:opacity-60 ${offersEnabled ? 'bg-amber-50 text-amber-600 border-amber-100 hover:bg-amber-100' : 'bg-green-50 text-green-600 border-green-100 hover:bg-green-100'}`}
										>
											{offersLoading ? 'Saving...' : offersEnabled ? 'Hide Offers' : 'Show Offers'}
										</button>
									</div>
								</div>
							</div>
						</div>
					)}
			</div>

			{/* 2FA Setup Dialog */}
			<Dialog
				open={showSetupDialog}
				onClose={() => !setupLoading && setShowSetupDialog(false)}
				maxWidth="xs"
				fullWidth
				PaperProps={{
					sx: { borderRadius: '1.25rem' }
				}}
			>
				<DialogTitle sx={{ textAlign: 'center', pt: 4, pb: 0 }}>
					<div className="flex justify-center mb-4">
						<div className="p-3 bg-indigo-50 rounded-2xl">
							<QrCode className="w-10 h-10 text-[#523680]" />
						</div>
					</div>
					<span className="text-2xl font-bold text-gray-800">Set Up 2FA</span>
				</DialogTitle>
				<DialogContent sx={{ px: 4, pb: 4 }}>
					<p className="text-center text-gray-600 mb-6 text-sm">
						Scan this QR code with your Google Authenticator or similar 2FA app.
					</p>

					{setupData && (
						<div className="flex flex-col items-center">
							<div className="p-4 bg-white border-2 border-dashed border-gray-100 rounded-3xl mb-6">
								<img
									src={setupData.qrCodeDataURL}
									alt="2FA QR Code"
									className="w-48 h-48"
								/>
							</div>

							<div className="w-full mb-8">
								<label className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 block">
									Manual Entry Key
								</label>
								<div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
									<code className="text-sm font-mono text-gray-800 flex-1 truncate">
										{setupData.secret}
									</code>
									<button
										onClick={copySecret}
										className="p-1.5 hover:bg-white rounded-lg transition-colors text-gray-500"
									>
										<Copy className="w-4 h-4" />
									</button>
								</div>
							</div>

							<div className="w-full">
								<label className="text-sm font-medium text-gray-700 mb-2 block">
									Verification Code
								</label>
								<TextField
									fullWidth
									placeholder="000 000"
									value={verificationCode}
									onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
									variant="outlined"
									autoFocus
									InputProps={{
										sx: {
											borderRadius: '0.75rem',
											letterSpacing: '0.25em',
											textAlign: 'center',
											fontSize: '1.25rem',
											fontWeight: 600
										}
									}}
								/>
							</div>
						</div>
					)}
				</DialogContent>
				<DialogActions sx={{ px: 4, pb: 4, pt: 0 }}>
					<Button
						onClick={() => setShowSetupDialog(false)}
						sx={{ color: 'gray', textTransform: 'none', fontWeight: 600 }}
					>
						Cancel
					</Button>
					<Button
						onClick={verifyAndEnable}
						disabled={setupLoading || verificationCode.length !== 6}
						sx={{
							backgroundColor: '#523680',
							color: 'white',
							px: 4,
							borderRadius: '0.75rem',
							textTransform: 'none',
							fontWeight: 600,
							'&:hover': { backgroundColor: '#3f2a60' },
							'&:disabled': { opacity: 0.6 }
						}}
					>
						{setupLoading ? 'Verifying...' : 'Finish Setup'}
					</Button>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert
					onClose={() => setSnackbar({ ...snackbar, open: false })}
					severity={snackbar.severity}
					variant="filled"
					sx={{ width: '100%', borderRadius: '0.75rem' }}
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</div>
	);
};

export default Settings;
